import { Input, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { RestClient } from '../../../api/restClient';
import { TemplateService } from '../services/templateService';
import { TemplateDto } from '../types/template';

const TemplateListContainer = styled.div`
  margin-top: 20px;

  .ant-table-wrapper {
    .ant-table {
      font-size: 14px;
    }

    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 600;
    }

    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
`;

interface TemplateListProps {
  onSelectionChange?: (selectedTemplateIds: string[]) => void;
  restClient: RestClient;
}

export const TemplateList = observer(
  ({ onSelectionChange, restClient }: TemplateListProps) => {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState<TemplateDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const filteredTemplates = useMemo(() => {
      if (!searchQuery.trim()) return templates;
      const query = searchQuery.trim().toLowerCase();
      return templates.filter((tpl) =>
        (tpl.title ?? '').toLowerCase().includes(query)
      );
    }, [templates, searchQuery]);

    useEffect(() => {
      const fetchTemplates = async () => {
        setLoading(true);
        try {
          const templateService = new TemplateService(restClient);
          const result = await templateService.getGuidedDoingTemplates();

          if (result.succeeded && result.data) {
            setTemplates(result.data);
          } else {
            console.error('Failed to fetch templates:', result.message);
          }
        } catch (error) {
          console.error('Error fetching templates:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchTemplates();
    }, [restClient]);

    const columns = [
      {
        title: t('admOrgs:GuidoLibraryModal:Table:titleColumn'),
        dataIndex: 'title',
        key: 'title',
        width: '40%',
      },
      {
        title: t('admOrgs:GuidoLibraryModal:Table:previewColumn'),
        dataIndex: 'previewUrl',
        key: 'previewUrl',
        width: '40%',
        render: (previewUrl: string) =>
          previewUrl ? (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer">
              {t('admOrgs:GuidoLibraryModal:Table:viewPreview')}
            </a>
          ) : (
            <span>{t('admOrgs:GuidoLibraryModal:Table:noPreview')}</span>
          ),
      },
      {
        title: t('admOrgs:GuidoLibraryModal:Table:defaultColumn'),
        key: 'defaults',
        width: '20%',
        render: (_: unknown, record: TemplateDto) => {
          const parts: string[] = [];
          if (record.firstDefault) {
            parts.push(t('admOrgs:GuidoLibraryModal:Table:firstDefault', 'First'));
          }
          if (record.secondDefault) {
            parts.push(t('admOrgs:GuidoLibraryModal:Table:secondDefault', 'Second'));
          }
          return parts.length > 0
            ? parts.join(', ')
            : t('admOrgs:GuidoLibraryModal:Table:no');
        },
      },
    ];

    const rowSelection = {
      selectedRowKeys,
      onChange: (selectedKeys: React.Key[]) => {
        setSelectedRowKeys(selectedKeys);
        if (onSelectionChange) {
          onSelectionChange(selectedKeys as string[]);
        }
      },
    };

    return (
      <TemplateListContainer>
        <Input
          placeholder={t('admOrgs:GuidoLibraryModal:Table:searchPlaceholder', 'Search by guido title...')}
          prefix={<SearchOutlined />}
          allowClear
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ marginBottom: 12, maxWidth: 320 }}
        />
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredTemplates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              t('admOrgs:GuidoLibraryModal:Table:pagination', {
                start: range[0],
                end: range[1],
                total,
              }),
          }}
          locale={{
            emptyText: t('admOrgs:GuidoLibraryModal:Table:noData'),
          }}
        />
      </TemplateListContainer>
    );
  }
);
